import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  bulkUpdateQBInvoiceSmsOverage,
  bulkChargeStripeCustomerForInvoice,
  bulkSendQBInvoiceToCustomer,
  bulkCreateQuickBooksPaymentForInvoices,
  bulkUpdateQBInvoiceCustomerCountOverage,
} from "../../../../shared/billing.api.js";

// Helper functions to check if action already done
function hasAlreadyUpdatedSmsOverage(row) {
  return row?.changelogs?.some(
    (log) =>
      log.action === "update_sms_overage" && log.result_status === "success",
  );
}
function hasAlreadyChargedCustomer(row) {
  return row?.changelogs?.some(
    (log) =>
      log.action === "charge_stripe_customer" &&
      log.result_status === "success",
  );
}
function hasAlreadySentInvoice(row) {
  return (
    row?.invoice?.EmailStatus === "EmailSent" ||
    row?.invoice?.EInvoiceStatus === "Sent" ||
    row?.changelogs?.some(
      (log) =>
        log.result_status === "success" &&
        log.action.includes("send_customer_invoice"),
    )
  );
}
function hasAlreadyMarkedPaid(row) {
  return (
    row?.invoice?.Balance === 0 ||
    row?.changelogs?.some(
      (log) =>
        log.action === "create_quickbooks_payment" &&
        log.result_status === "success",
    )
  );
}

function hasAlreadyUpdatedCustomerCountOverage(row) {
  return row?.changelogs?.some(
    (log) =>
      log.action === "add_customer_count_usage" &&
      log.result_status === "success",
  );
}

// Helper for result color and JSON dump (like single-action)
function getResultColor(result) {
  return "inherit";
  /*   if (!result) return "inherit";
  if (
    result.status === "success" ||
    result.status_code === 200 ||
    result.response?.status === "success" ||
    result.response?.status === "Charge successful"
  )
    return "success.main";
  return "error.main"; */
}
export function isResultSuccess(result) {
  if (!result) return false;
  return (
    result.status === "success" ||
    result.status_code === 200 ||
    result.response?.status === "success" ||
    result.response?.status === "Charge successful"
  );
}

function getResultText(result) {
  if (!result) return "";
  if (isResultSuccess(result)) {
    return typeof result.response === "object"
      ? JSON.stringify(result.response, null, 2)
      : String(result.response);
  }
  // Always dump JSON if not success
  return JSON.stringify(result, null, 2);
}

const MAX_RESULT_HEIGHT = 120;

// Helper to get display name for invoice
function getInvoiceDisplayName(row) {
  return row?.invoice?.DocNumber
    ? `QuickBooks Doc Number ${row.invoice.DocNumber}`
    : `QuickBooks Invoice ${row.invoice?.Id ?? "--"}`;
}

// Bulk logic and modals hook
export function useBulkInvoiceActions({ snackbarRef }) {
  // Bulk SMS Overage
  const [bulkSmsOverageModalOpen, setBulkSmsOverageModalOpen] = useState(false);
  const [bulkSmsOverageSelected, setBulkSmsOverageSelected] = useState([]);
  const [bulkSmsOverageLoading, setBulkSmsOverageLoading] = useState(false);
  const [bulkSmsOverageResults, setBulkSmsOverageResults] = useState([]);
  const [bulkSmsOverageCallback, setBulkSmsOverageCallback] = useState(null);

  // Bulk Charge
  const [bulkChargeModalOpen, setBulkChargeModalOpen] = useState(false);
  const [bulkChargeSelected, setBulkChargeSelected] = useState([]);
  const [bulkChargeLoading, setBulkChargeLoading] = useState(false);
  const [bulkChargeResults, setBulkChargeResults] = useState([]);
  const [bulkChargeCallback, setBulkChargeCallback] = useState(null);

  // Bulk Send
  const [bulkSendModalOpen, setBulkSendModalOpen] = useState(false);
  const [bulkSendSelected, setBulkSendSelected] = useState([]);
  const [bulkSendLoading, setBulkSendLoading] = useState(false);
  const [bulkSendResults, setBulkSendResults] = useState([]);
  const [bulkSendCallback, setBulkSendCallback] = useState(null);

  // Bulk Mark Paid
  const [bulkMarkPaidModalOpen, setBulkMarkPaidModalOpen] = useState(false);
  const [bulkMarkPaidSelected, setBulkMarkPaidSelected] = useState([]);
  const [bulkMarkPaidLoading, setBulkMarkPaidLoading] = useState(false);
  const [bulkMarkPaidResults, setBulkMarkPaidResults] = useState([]);
  const [bulkMarkPaidCallback, setBulkMarkPaidCallback] = useState(null);

  const [
    bulkCustomerCountOverageModalOpen,
    setBulkCustomerCountOverageModalOpen,
  ] = useState(false);
  const [
    bulkCustomerCountOverageSelected,
    setBulkCustomerCountOverageSelected,
  ] = useState([]);
  const [bulkCustomerCountOverageLoading, setBulkCustomerCountOverageLoading] =
    useState(false);
  const [bulkCustomerCountOverageResults, setBulkCustomerCountOverageResults] =
    useState([]);
  const [
    bulkCustomerCountOverageCallback,
    setBulkCustomerCountOverageCallback,
  ] = useState(null);

  // Add a flag to track if API was called for each modal
  const [bulkSmsOverageApiCalled, setBulkSmsOverageApiCalled] = useState(false);
  const [bulkChargeApiCalled, setBulkChargeApiCalled] = useState(false);
  const [bulkSendApiCalled, setBulkSendApiCalled] = useState(false);
  const [bulkMarkPaidApiCalled, setBulkMarkPaidApiCalled] = useState(false);
  const [
    bulkCustomerCountOverageApiCalled,
    setBulkCustomerCountOverageApiCalled,
  ] = useState(false);

  // Handlers
  function handleBulkSmsOverageUpdate(selected, callback) {
    setBulkSmsOverageSelected(selected);
    setBulkSmsOverageModalOpen(true);
    setBulkSmsOverageResults([]);
    setBulkSmsOverageCallback(() => callback);
    setBulkSmsOverageApiCalled(false); // Reset flag
  }
  function confirmBulkSmsOverageUpdate() {
    setBulkSmsOverageLoading(true);
    setBulkSmsOverageResults([]);
    setBulkSmsOverageApiCalled(true); // Mark API as called
    const updates = bulkSmsOverageSelected.map((row) => ({
      invoiceId: row.invoice.Id,
      smsOverage:
        row?.overages_and_usage?.usage_sms_credits?.current?.overage_usage || 0,
      overagePerUnit:
        row?.overages_and_usage?.usage_sms_credits?.current?.overage_per_unit ||
        0,
    }));
    bulkUpdateQBInvoiceSmsOverage({ updates })
      .then(
        handleBulkApiResponse(
          setBulkSmsOverageLoading,
          setBulkSmsOverageResults,
          snackbarRef.current?.addSnackbar,
          "SMS overage update",
        ),
      )
      .catch(
        handleBulkApiError(
          setBulkSmsOverageLoading,
          setBulkSmsOverageResults,
          snackbarRef.current?.addSnackbar,
          "SMS overage update",
        ),
      );
  }

  function handleBulkCustomerCountOverageUpdate(selected, callback) {
    setBulkCustomerCountOverageSelected(selected);
    setBulkCustomerCountOverageModalOpen(true);
    setBulkCustomerCountOverageResults([]);
    setBulkCustomerCountOverageCallback(() => callback);
    setBulkCustomerCountOverageApiCalled(false);
  }
  function confirmBulkCustomerCountOverageUpdate() {
    setBulkCustomerCountOverageLoading(true);
    setBulkCustomerCountOverageResults([]);
    setBulkCustomerCountOverageApiCalled(true);
    const updates = bulkCustomerCountOverageSelected.map((row) => ({
      invoiceId: row.invoice.Id,
      customerCountUsage:
        row?.overages_and_usage?.additional_usage_base_contacts?.current
          ?.overage_usage || 0,
      overagePerUnit:
        row?.overages_and_usage?.additional_usage_base_contacts?.current
          ?.overage_per_unit || 0,
    }));
    bulkUpdateQBInvoiceCustomerCountOverage({ updates })
      .then(
        handleBulkApiResponse(
          setBulkCustomerCountOverageLoading,
          setBulkCustomerCountOverageResults,
          snackbarRef.current?.addSnackbar,
          "Customer Count overage update",
        ),
      )
      .catch(
        handleBulkApiError(
          setBulkCustomerCountOverageLoading,
          setBulkCustomerCountOverageResults,
          snackbarRef.current?.addSnackbar,
          "Customer Count overage update",
        ),
      );
  }

  function handleBulkChargeStripeCustomers(selected, callback) {
    setBulkChargeSelected(selected);
    setBulkChargeModalOpen(true);
    setBulkChargeResults([]);
    setBulkChargeCallback(() => callback);
    setBulkChargeApiCalled(false);
  }
  function confirmBulkChargeStripeCustomers() {
    setBulkChargeLoading(true);
    setBulkChargeResults([]);
    setBulkChargeApiCalled(true);
    const invoiceIds = bulkChargeSelected.map((row) => row.invoice.Id);
    bulkChargeStripeCustomerForInvoice({ invoiceIds })
      .then(
        handleBulkApiResponse(
          setBulkChargeLoading,
          setBulkChargeResults,
          snackbarRef.current?.addSnackbar,
          "Stripe charge",
        ),
      )
      .catch(
        handleBulkApiError(
          setBulkChargeLoading,
          setBulkChargeResults,
          snackbarRef.current?.addSnackbar,
          "Stripe charge",
        ),
      );
  }

  function handleBulkSendInvoicesToCustomers(selected, callback) {
    setBulkSendSelected(selected);
    setBulkSendModalOpen(true);
    setBulkSendResults([]);
    setBulkSendCallback(() => callback);
    setBulkSendApiCalled(false);
  }
  function confirmBulkSendInvoicesToCustomers() {
    setBulkSendLoading(true);
    setBulkSendResults([]);
    setBulkSendApiCalled(true);
    const invoiceIds = bulkSendSelected.map((row) => row.invoice.Id);
    bulkSendQBInvoiceToCustomer({ invoiceIds })
      .then(
        handleBulkApiResponse(
          setBulkSendLoading,
          setBulkSendResults,
          snackbarRef.current?.addSnackbar,
          "send invoices",
        ),
      )
      .catch(
        handleBulkApiError(
          setBulkSendLoading,
          setBulkSendResults,
          snackbarRef.current?.addSnackbar,
          "send invoices",
        ),
      );
  }

  function handleBulkMarkInvoicesPaid(selected, callback) {
    setBulkMarkPaidSelected(selected);
    setBulkMarkPaidModalOpen(true);
    setBulkMarkPaidResults([]);
    setBulkMarkPaidCallback(() => callback);
    setBulkMarkPaidApiCalled(false);
  }
  function confirmBulkMarkInvoicesPaid() {
    setBulkMarkPaidLoading(true);
    setBulkMarkPaidResults([]);
    setBulkMarkPaidApiCalled(true);
    const invoiceIds = bulkMarkPaidSelected.map((row) => row.invoice.Id);
    bulkCreateQuickBooksPaymentForInvoices({ invoiceIds })
      .then(
        handleBulkApiResponse(
          setBulkMarkPaidLoading,
          setBulkMarkPaidResults,
          snackbarRef.current?.addSnackbar,
          "mark invoices as paid",
        ),
      )
      .catch(
        handleBulkApiError(
          setBulkMarkPaidLoading,
          setBulkMarkPaidResults,
          snackbarRef.current?.addSnackbar,
          "mark invoices as paid",
        ),
      );
  }

  // --- Unified API response/error handling for bulk actions ---
  function handleBulkApiResponse(setLoading, setResults, snackbar, label) {
    return (resultsObj) => {
      setLoading(false);
      setResults(resultsObj?.data?.results || []);
      snackbar?.({
        type: "success",
        message: `Bulk ${label} complete.`,
      });
    };
  }
  function handleBulkApiError(setLoading, setResults, snackbar, label) {
    return (e) => {
      setLoading(false);
      setResults([
        { invoice_id: "N/A", status: "error", message: e.message, error: e },
      ]);
      snackbar?.({
        type: "error",
        message: `Error during bulk ${label}: ${e.message}`,
      });
    };
  }

  // Helper: get warnings for all previous steps (based on current bulk modal)
  function getBulkPreviousStepWarnings(row, steps) {
    const warnings = [];
    if (steps.includes("sms") && !hasAlreadyUpdatedSmsOverage(row)) {
      warnings.push(
        <Typography variant="body2" color="warning.main" key="sms">
          <strong>Warning:</strong> SMS overage has not been updated for this
          invoice.
        </Typography>,
      );
    }
    if (steps.includes("charge") && !hasAlreadyChargedCustomer(row)) {
      warnings.push(
        <Typography variant="body2" color="warning.main" key="charge">
          <strong>Warning:</strong> Customer has not been charged for this
          invoice.
        </Typography>,
      );
    }
    if (steps.includes("send") && !hasAlreadySentInvoice(row)) {
      warnings.push(
        <Typography variant="body2" color="warning.main" key="send">
          <strong>Warning:</strong> Invoice has not been sent to the customer.
        </Typography>,
      );
    }
    if (steps.includes("paid") && !hasAlreadyMarkedPaid(row)) {
      warnings.push(
        <Typography variant="body2" color="warning.main" key="paid">
          <strong>Warning:</strong> Invoice has not been marked as paid in
          QuickBooks.
        </Typography>,
      );
    }
    return warnings;
  }

  // Bulk modals
  const modals = (
    <>
      {/* Bulk SMS Overage Confirmation Modal */}
      <Dialog
        open={bulkSmsOverageModalOpen}
        onClose={(e, r) => {
          e.stopPropagation();
          if (r == "backdropClick") {
            return;
          }
          setBulkSmsOverageModalOpen(false);
          if (bulkSmsOverageCallback)
            bulkSmsOverageCallback(bulkSmsOverageApiCalled);
          setBulkSmsOverageResults([]);
          setBulkSmsOverageCallback(null);
          setBulkSmsOverageApiCalled(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Bulk SMS Overage Update</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to update SMS overage for the following
            invoices?
          </Typography>
          <Box mt={2}>
            <Stack spacing={1}>
              {[...bulkSmsOverageSelected]
                .sort((a, b) => {
                  const aMissing = !a.accountName ? 1 : 0;
                  const bMissing = !b.accountName ? 1 : 0;
                  const aDone = hasAlreadyUpdatedSmsOverage(a) ? 1 : 0;
                  const bDone = hasAlreadyUpdatedSmsOverage(b) ? 1 : 0;
                  // Highest priority: both missing and done
                  const aPriority =
                    aMissing && aDone ? 2 : aMissing ? 1 : aDone ? 1 : 0;
                  const bPriority =
                    bMissing && bDone ? 2 : bMissing ? 1 : bDone ? 1 : 0;
                  if (bPriority !== aPriority) {
                    return bPriority - aPriority;
                  }
                  // Secondary sort by DocNumber or Id for stable ordering
                  return (
                    (a.invoice?.DocNumber || a.invoice?.Id || 0) -
                    (b.invoice?.DocNumber || b.invoice?.Id || 0)
                  );
                })
                .map((row, idx) => {
                  const alreadyDone = hasAlreadyUpdatedSmsOverage(row);
                  const result =
                    bulkSmsOverageResults?.find(
                      (res) => res.invoice_id === row.invoice?.Id,
                    ) || null;
                  return (
                    <Box
                      key={row.invoice?.Id || idx}
                      sx={{
                        borderBottom: "1px solid #eee",
                        pb: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Invoice:</strong> {getInvoiceDisplayName(row)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Company/Account Name:</strong>{" "}
                        {row.invoice?.CustomerRef?.name ?? "--"}/
                        {row.accountName ?? "--"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>SMS Overage:</strong>{" "}
                        {typeof row.overages_and_usage?.usage_sms_credits
                          ?.current?.overage_usage === "number"
                          ? `${row.overages_and_usage.usage_sms_credits.current.overage_usage}`
                          : "--"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>SMS Overage Per Unit Cost:</strong>{" "}
                        {typeof row.overages_and_usage?.usage_sms_credits
                          ?.current?.overage_per_unit === "number"
                          ? `$${row.overages_and_usage.usage_sms_credits.current.overage_per_unit.toFixed(
                              2,
                            )}`
                          : "--"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Total Overage Cost:</strong>{" "}
                        {Number(row.overages_and_usage?.usage_sms_credits?.current?.overage_usage) &&
                        Number(row.overages_and_usage?.usage_sms_credits?.current?.overage_per_unit)
                          ? `$${(
                              Number(row.overages_and_usage.usage_sms_credits.current.overage_usage) *
                              Number(row.overages_and_usage.usage_sms_credits.current.overage_per_unit)
                            ).toFixed(2)}`
                          : "--"}
                      </Typography>

                      {alreadyDone && (
                        <Typography variant="body2" color="error.main">
                          <em>
                            Error: This invoice has already had its SMS overage
                            updated.
                          </em>
                        </Typography>
                      )}
                      {!row.accountName && (
                        <Typography variant="body2" color="error.main">
                          <em>
                            Error: Cannot update SMS overage - missing account.
                          </em>
                        </Typography>
                      )}

                      {result && (
                        <Box mt={1}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: getResultColor(result),
                              maxHeight: MAX_RESULT_HEIGHT,
                              overflowY: "auto",
                              background: "#f5f5f5",
                              borderRadius: 1,
                              p: 1,
                              fontSize: "12px",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            <strong>Result:</strong> {getResultText(result)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
            </Stack>
            {bulkSmsOverageLoading && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mt: 2 }}
              >
                <CircularProgress size={24} />
                <Typography variant="body2">
                  Updating SMS overages...
                </Typography>
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBulkSmsOverageModalOpen(false);
              if (bulkSmsOverageCallback)
                bulkSmsOverageCallback(bulkSmsOverageApiCalled);
              setBulkSmsOverageResults([]);
              setBulkSmsOverageCallback(null);
              setBulkSmsOverageApiCalled(false);
            }}
            disabled={bulkSmsOverageLoading}
          >
            Close
          </Button>
          {bulkSmsOverageResults?.length == 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={confirmBulkSmsOverageUpdate}
              disabled={
                bulkSmsOverageLoading ||
                bulkSmsOverageSelected.some((row) => !row.accountName) ||
                bulkSmsOverageSelected.some((row) =>
                  hasAlreadyUpdatedSmsOverage(row),
                )
              }
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Bulk Charge Confirmation Modal */}
      <Dialog
        open={bulkChargeModalOpen}
        onClose={(e, r) => {
          e.stopPropagation();
          if (r == "backdropClick") {
            return;
          }
          setBulkChargeModalOpen(false);
          if (bulkChargeCallback)
            bulkChargeCallback(bulkChargeApiCalled, bulkChargeResults);
          setBulkChargeResults([]);
          setBulkChargeCallback(null);
          setBulkChargeApiCalled(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Bulk Stripe Charge</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to charge the following invoices to their
            Stripe customers?
          </Typography>
          <Box mt={2}>
            <Stack spacing={1}>
              {[...bulkChargeSelected]
                .sort((a, b) => {
                  const aDisabled =
                    !a?.contract_third_party_info.stripe_customer_id ||
                    hasAlreadyChargedCustomer(a) ||
                    (typeof a.quickbooks_customer?.Balance === "number" &&
                      a.quickbooks_customer.Balance <= 0) ||
                    (typeof a.invoice?.TotalAmt === "number" &&
                      a.invoice.TotalAmt <= 0);
                  const bDisabled =
                    !b?.contract_third_party_info.stripe_customer_id ||
                    hasAlreadyChargedCustomer(b) ||
                    (typeof b.quickbooks_customer?.Balance === "number" &&
                      b.quickbooks_customer.Balance <= 0) ||
                    (typeof b.invoice?.TotalAmt === "number" &&
                      b.invoice.TotalAmt <= 0);
                  if (Number(bDisabled) !== Number(aDisabled)) {
                    return Number(bDisabled) - Number(aDisabled);
                  }
                  // Secondary sort by DocNumber or Id for stable ordering
                  return (
                    (a.invoice?.DocNumber || a.invoice?.Id || 0) -
                    (b.invoice?.DocNumber || b.invoice?.Id || 0)
                  );
                })
                .map((row, idx) => {
                  const stripeMissing =
                    !row?.contract_third_party_info.stripe_customer_id;
                  const alreadyDone = hasAlreadyChargedCustomer(row);
                  const result =
                    bulkChargeResults.find(
                      (res) => res.invoice_id === row.invoice?.Id,
                    ) || null;
                  const customerBalance =
                    typeof row.quickbooks_customer?.Balance === "number"
                      ? row.quickbooks_customer.Balance
                      : null;
                  const invoiceTotal =
                    typeof row.invoice?.TotalAmt === "number"
                      ? row.invoice.TotalAmt
                      : null;
                  return (
                    <Box
                      key={row.invoice?.Id || idx}
                      sx={{
                        borderBottom: "1px solid #eee",
                        pb: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Invoice:</strong> {getInvoiceDisplayName(row)}
                      </Typography>
                      <Typography
                        variant="body2"
                        color={stripeMissing ? "error" : "inherit"}
                      >
                        <strong>Company/Stripe Customer:</strong>{" "}
                        {row.invoice?.CustomerRef?.name ?? "--"}/
                        {row?.contract_third_party_info.stripe_customer_id ??
                          "--"}
                        {stripeMissing && (
                          <span style={{ color: "red", marginLeft: 8 }}>
                            (Missing Stripe Customer)
                          </span>
                        )}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Total Invoice Amount:</strong>{" "}
                        {invoiceTotal !== null
                          ? `$${invoiceTotal.toFixed(2)}`
                          : "--"}
                      </Typography>
                      {customerBalance !== null && (
                        <Box mt={1}>
                          <Typography variant="body2">
                            <strong>Customer Balance:</strong>{" "}
                            {`$${customerBalance.toFixed(2)}`}
                          </Typography>
                        </Box>
                      )}
                      <Box mt={2}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          fontSize={18}
                        >
                          <strong>Actual Amount to Bill via Stripe:</strong>{" "}
                          {invoiceTotal !== null && customerBalance !== null
                            ? `$${Math.min(
                                invoiceTotal,
                                customerBalance,
                              ).toFixed(2)}`
                            : "--"}
                        </Typography>
                      </Box>
                      {customerBalance !== null && customerBalance <= 0 && (
                        <Box mt={1}>
                          <Typography
                            color="error.main"
                            variant="body2"
                            fontWeight="bold"
                          >
                            <strong>Cannot charge customer:</strong> Customer
                            balance is zero or negative.
                          </Typography>
                        </Box>
                      )}
                      {/* Previous step warnings for bulk charge */}
                      {getBulkPreviousStepWarnings(row, ["sms"])}
                      {alreadyDone && (
                        <Typography variant="body2" color="error.main">
                          <em>
                            Error: This invoice has already been charged to the
                            customer.
                          </em>
                        </Typography>
                      )}
                      {result && (
                        <Box mt={1}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: getResultColor(result),
                              maxHeight: MAX_RESULT_HEIGHT,
                              overflowY: "auto",
                              background: "#f5f5f5",
                              borderRadius: 1,
                              p: 1,
                              fontSize: "12px",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            <strong>Result:</strong> {getResultText(result)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
            </Stack>
            {bulkChargeLoading && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mt: 2 }}
              >
                <CircularProgress size={24} />
                <Typography variant="body2">
                  Charging Stripe customers...
                </Typography>
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBulkChargeModalOpen(false);
              if (bulkChargeCallback)
            bulkChargeCallback(bulkChargeApiCalled, bulkChargeResults);
              setBulkChargeResults([]);
              setBulkChargeCallback(null);
              setBulkChargeApiCalled(false);
            }}
            disabled={bulkChargeLoading}
          >
            Close
          </Button>
          {bulkChargeResults.length == 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={confirmBulkChargeStripeCustomers}
              disabled={
                bulkChargeLoading ||
                bulkChargeSelected.some(
                  (row) =>
                    !row?.contract_third_party_info.stripe_customer_id ||
                    hasAlreadyChargedCustomer(row) ||
                    (typeof row.quickbooks_customer?.Balance === "number" &&
                      row.quickbooks_customer.Balance <= 0) ||
                    (typeof row.invoice?.TotalAmt === "number" &&
                      row.invoice.TotalAmt <= 0),
                )
              }
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Bulk Mark as Paid Confirmation Modal */}
      <Dialog
        open={bulkMarkPaidModalOpen}
        onClose={(e, r) => {
          e.stopPropagation();
          if (r == "backdropClick") {
            return;
          }
          setBulkMarkPaidModalOpen(false);
          if (bulkMarkPaidCallback)
            bulkMarkPaidCallback(bulkMarkPaidApiCalled, bulkMarkPaidResults);
          setBulkMarkPaidResults([]);
          setBulkMarkPaidCallback(null);
          setBulkMarkPaidApiCalled(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Bulk Mark Invoices as Paid</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to mark the following invoices as paid in
            QuickBooks?
          </Typography>
          <Box mt={2}>
            <Stack spacing={1}>
              {[...bulkMarkPaidSelected]
                .sort((a, b) => {
                  const aDisabled =
                    hasAlreadyMarkedPaid(a) ||
                    (typeof a.quickbooks_customer?.Balance === "number" &&
                      a.quickbooks_customer.Balance <= 0) ||
                    (typeof a.invoice?.TotalAmt === "number" &&
                      a.invoice.TotalAmt <= 0);
                  const bDisabled =
                    hasAlreadyMarkedPaid(b) ||
                    (typeof b.quickbooks_customer?.Balance === "number" &&
                      b.quickbooks_customer.Balance <= 0) ||
                    (typeof b.invoice?.TotalAmt === "number" &&
                      b.invoice.TotalAmt <= 0);
                  if (Number(bDisabled) !== Number(aDisabled)) {
                    return Number(bDisabled) - Number(aDisabled);
                  }
                  // Secondary sort by DocNumber or Id for stable ordering
                  return (
                    (a.invoice?.DocNumber || a.invoice?.Id || 0) -
                    (b.invoice?.DocNumber || b.invoice?.Id || 0)
                  );
                })
                .map((row, idx) => {
                  // const accountMissing = !row.accountName;
                  const alreadyDone = hasAlreadyMarkedPaid(row);
                  const result =
                    bulkMarkPaidResults.find(
                      (res) => res.invoice_id === row.invoice?.Id,
                    ) || null;
                  const customerBalance =
                    typeof row.quickbooks_customer?.Balance === "number"
                      ? row.quickbooks_customer.Balance
                      : null;
                  const invoiceTotal =
                    typeof row.invoice?.TotalAmt === "number"
                      ? row.invoice.TotalAmt
                      : null;
                  return (
                    <Box
                      key={row.invoice?.Id || idx}
                      sx={{
                        borderBottom: "1px solid #eee",
                        pb: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Invoice:</strong> {getInvoiceDisplayName(row)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Company/Account Name:</strong>{" "}
                        {row.invoice?.CustomerRef?.name ?? "--"}/
                        {row.accountName ?? "--"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Total Amount:</strong>{" "}
                        {invoiceTotal !== null
                          ? `$${invoiceTotal.toFixed(2)}`
                          : "--"}
                      </Typography>
                      {customerBalance !== null && (
                        <Box mt={1}>
                          <Typography variant="body2">
                            <strong>Customer Balance:</strong>{" "}
                            {`$${customerBalance.toFixed(2)}`}
                          </Typography>
                        </Box>
                      )}
                      <Box mt={2}>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          fontSize={18}
                        >
                          <strong>Amount to Actually Mark as Paid:</strong>{" "}
                          {invoiceTotal !== null && customerBalance !== null
                            ? `$${Math.min(
                                invoiceTotal,
                                customerBalance,
                              ).toFixed(2)}`
                            : "--"}
                        </Typography>
                      </Box>
                      {customerBalance !== null && customerBalance <= 0 && (
                        <Box mt={1}>
                          <Typography
                            color="error.main"
                            variant="body2"
                            fontWeight="bold"
                          >
                            <strong>Cannot mark as paid:</strong> Customer
                            balance is negative. Please resolve the balance
                            before proceeding.
                          </Typography>
                        </Box>
                      )}
                      {/* Previous step warnings for bulk mark paid */}
                      {getBulkPreviousStepWarnings(row, ["sms", "charge"])}
                      {alreadyDone && (
                        <Typography variant="body2" color="error.main">
                          <em>
                            Error: This invoice has already been marked as paid
                            in QuickBooks.
                          </em>
                        </Typography>
                      )}
                      {result && (
                        <Box mt={1}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: getResultColor(result),
                              maxHeight: MAX_RESULT_HEIGHT,
                              overflowY: "auto",
                              background: "#f5f5f5",
                              borderRadius: 1,
                              p: 1,
                              fontSize: "12px",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            <strong>Result:</strong> {getResultText(result)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
            </Stack>
            {bulkMarkPaidLoading && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mt: 2 }}
              >
                <CircularProgress size={24} />
                <Typography variant="body2">
                  Marking invoices as paid...
                </Typography>
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBulkMarkPaidModalOpen(false);
              if (bulkMarkPaidCallback)
                bulkMarkPaidCallback(bulkMarkPaidApiCalled, bulkMarkPaidResults);
              setBulkMarkPaidResults([]);
              setBulkMarkPaidCallback(null);
              setBulkMarkPaidApiCalled(false);
            }}
            disabled={bulkMarkPaidLoading}
          >
            Close
          </Button>
          {bulkMarkPaidResults.length == 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={confirmBulkMarkInvoicesPaid}
              disabled={
                bulkMarkPaidLoading ||
                bulkMarkPaidSelected.some(
                  (row) =>
                    hasAlreadyMarkedPaid(row) ||
                    (typeof row.quickbooks_customer?.Balance === "number" &&
                      row.quickbooks_customer.Balance <= 0) ||
                    (typeof row.invoice?.TotalAmt === "number" &&
                      row.invoice.TotalAmt <= 0),
                )
              }
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Bulk Send Confirmation Modal */}
      <Dialog
        open={bulkSendModalOpen}
        onClose={(e, r) => {
          e.stopPropagation();
          if (r == "backdropClick") {
            return;
          }
          setBulkSendModalOpen(false);
          if (bulkSendCallback)
            bulkSendCallback(bulkSendApiCalled, bulkSendResults);
          setBulkSendResults([]);
          setBulkSendCallback(null);
          setBulkSendApiCalled(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Bulk Send Invoices</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to send the following invoices to their
            customers?
          </Typography>
          <Box mt={2}>
            <Stack spacing={1}>
              {bulkSendSelected
                .sort((a, b) => {
                  const aDone = hasAlreadySentInvoice(a) ? 1 : 0;
                  const bDone = hasAlreadySentInvoice(b) ? 1 : 0;
                  return bDone - aDone; // true first
                })
                .map((row, idx) => {
                  const alreadyDone = hasAlreadySentInvoice(row);
                  const result =
                    bulkSendResults.find(
                      (res) => res.invoice_id === row.invoice?.Id,
                    ) || null;
                  return (
                    <Box
                      key={row.invoice?.Id || idx}
                      sx={{
                        borderBottom: "1px solid #eee",
                        pb: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Invoice:</strong> {getInvoiceDisplayName(row)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Company/Account Name:</strong>{" "}
                        {row.invoice?.CustomerRef?.name ?? "--"}/
                        {row.accountName ?? "--"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Total Amount:</strong>{" "}
                        {typeof row.invoice?.TotalAmt === "number"
                          ? `$${row.invoice.TotalAmt.toFixed(2)}`
                          : "--"}
                      </Typography>
                      {/* Previous step warnings for bulk send */}
                      {getBulkPreviousStepWarnings(row, [
                        "sms",
                        "charge",
                        "paid",
                      ])}
                      {alreadyDone && (
                        <Typography
                          variant="body2"
                          color="error.main"
                          fontSize="14px"
                          fontWeight={"bold"}
                        >
                          <em>
                            Error: This invoice has already been sent to the
                            customer.
                          </em>
                        </Typography>
                      )}
                      {result && (
                        <Box mt={1}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: getResultColor(result),
                              maxHeight: MAX_RESULT_HEIGHT,
                              overflowY: "auto",
                              background: "#f5f5f5",
                              borderRadius: 1,
                              p: 1,
                              fontSize: "12px",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            <strong>Result:</strong> {getResultText(result)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
            </Stack>
            {bulkSendLoading && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mt: 2 }}
              >
                <CircularProgress size={24} />
                <Typography variant="body2">
                  Sending invoices to customers...
                </Typography>
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBulkSendModalOpen(false);
              if (bulkSendCallback)
            bulkSendCallback(bulkSendApiCalled, bulkSendResults);
              setBulkSendResults([]);
              setBulkSendCallback(null);
              setBulkSendApiCalled(false);
            }}
            disabled={bulkSendLoading}
          >
            Close
          </Button>
          {bulkSendResults.length == 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={confirmBulkSendInvoicesToCustomers}
              disabled={
                bulkSendLoading ||
                bulkSendSelected.some((row) => hasAlreadySentInvoice(row))
              }
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Bulk Customer Count Overage Confirmation Modal */}
      <Dialog
        open={bulkCustomerCountOverageModalOpen}
        onClose={(e, r) => {
          e.stopPropagation();
          if (r == "backdropClick") {
            return;
          }
          setBulkCustomerCountOverageModalOpen(false);
          if (bulkCustomerCountOverageCallback)
            bulkCustomerCountOverageCallback(bulkCustomerCountOverageApiCalled);
          setBulkCustomerCountOverageResults([]);
          setBulkCustomerCountOverageCallback(null);
          setBulkCustomerCountOverageApiCalled(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Bulk Customer Count Overage Update</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to update Customer Count overage for the
            following invoices?
          </Typography>
          <Box mt={2}>
            <Stack spacing={1}>
              {[...bulkCustomerCountOverageSelected]
                .sort((a, b) => {
                  // Sort by: (missing account && already done) first
                  const aMissing = !a.accountName ? 1 : 0;
                  const bMissing = !b.accountName ? 1 : 0;
                  const aDone = hasAlreadyUpdatedCustomerCountOverage(a)
                    ? 1
                    : 0;
                  const bDone = hasAlreadyUpdatedCustomerCountOverage(b)
                    ? 1
                    : 0;
                  // Highest priority: both missing and done
                  const aPriority =
                    aMissing && aDone ? 2 : aMissing ? 1 : aDone ? 1 : 0;
                  const bPriority =
                    bMissing && bDone ? 2 : bMissing ? 1 : bDone ? 1 : 0;
                  return bPriority - aPriority;
                })
                .map((row, idx) => {
                  const accountMissing = !row.accountName;
                  const alreadyDone =
                    hasAlreadyUpdatedCustomerCountOverage(row);
                  const result =
                    bulkCustomerCountOverageResults?.find(
                      (res) => res.invoice_id === row.invoice?.Id,
                    ) || null;
                  return (
                    <Box
                      key={row.invoice?.Id || idx}
                      sx={{
                        borderBottom: "1px solid #eee",
                        pb: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Invoice:</strong> {getInvoiceDisplayName(row)}
                      </Typography>
                      <Typography
                        variant="body2"
                        color={accountMissing ? "error" : "inherit"}
                      >
                        <strong>Company/Account Name:</strong>{" "}
                        {row.invoice?.CustomerRef?.name ?? "--"}/
                        {row.accountName ?? "--"}
                        {accountMissing && (
                          <span style={{ color: "red", marginLeft: 8 }}>
                            (Missing Account)
                          </span>
                        )}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Customer Count Overage:</strong>{" "}
                        {typeof row.overages_and_usage
                          ?.additional_usage_base_contacts?.current
                          ?.overage_usage === "number"
                          ? `${row.overages_and_usage.additional_usage_base_contacts.current.overage_usage}`
                          : "--"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Overage Per Unit Cost (10kmore):</strong>{" "}
                        {typeof row.overages_and_usage
                          ?.additional_usage_base_contacts?.current
                          ?.overage_per_unit === "number"
                          ? `$${row.overages_and_usage.additional_usage_base_contacts.current.overage_per_unit.toFixed(
                              2,
                            )}`
                          : "--"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Total Overage Cost:</strong>{" "}
                        {Number(
                          row.overages_and_usage?.additional_usage_base_contacts
                            ?.current?.overage_usage,
                        ) &&
                        Number(
                          row.overages_and_usage?.additional_usage_base_contacts
                            ?.current?.overage_per_unit,
                        )
                          ? `$${(
                              Number(
                                row.overages_and_usage
                                  .additional_usage_base_contacts.current
                                  .overage_usage,
                              ) *
                              Number(
                                row.overages_and_usage
                                  .additional_usage_base_contacts.current
                                  .overage_per_unit,
                              )
                            ).toFixed(2)}`
                          : "--"}
                      </Typography>

                      {alreadyDone && (
                        <Typography variant="body2" color="error.main">
                          <em>
                            Error: This invoice has already had its Customer
                            Count overage updated.
                          </em>
                        </Typography>
                      )}
                      {result && (
                        <Box mt={1}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: getResultColor(result),
                              maxHeight: MAX_RESULT_HEIGHT,
                              overflowY: "auto",
                              background: "#f5f5f5",
                              borderRadius: 1,
                              p: 1,
                              fontSize: "12px",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            <strong>Result:</strong> {getResultText(result)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
            </Stack>
            {bulkCustomerCountOverageLoading && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mt: 2 }}
              >
                <CircularProgress size={24} />
                <Typography variant="body2">
                  Updating Customer Count overages...
                </Typography>
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBulkCustomerCountOverageModalOpen(false);
              if (bulkCustomerCountOverageCallback)
                bulkCustomerCountOverageCallback(
                  bulkCustomerCountOverageApiCalled,
                );
              setBulkCustomerCountOverageResults([]);
              setBulkCustomerCountOverageCallback(null);
              setBulkCustomerCountOverageApiCalled(false);
            }}
            disabled={bulkCustomerCountOverageLoading}
          >
            Close
          </Button>
          {bulkCustomerCountOverageResults?.length == 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={confirmBulkCustomerCountOverageUpdate}
              disabled={
                bulkCustomerCountOverageLoading ||
                bulkCustomerCountOverageSelected.some(
                  (row) => !row.accountName,
                ) ||
                bulkCustomerCountOverageSelected.some((row) =>
                  hasAlreadyUpdatedCustomerCountOverage(row),
                )
              }
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );

  return {
    handleBulkSmsOverageUpdate,
    confirmBulkSmsOverageUpdate,
    handleBulkCustomerCountOverageUpdate,
    confirmBulkCustomerCountOverageUpdate,
    handleBulkChargeStripeCustomers,
    confirmBulkChargeStripeCustomers,
    handleBulkSendInvoicesToCustomers,
    confirmBulkSendInvoicesToCustomers,
    handleBulkMarkInvoicesPaid,
    confirmBulkMarkInvoicesPaid,
    modals,
  };
}
